'use client'

import { useQuery } from '@tanstack/react-query'

import { resolveAnalyticsBookByName } from '@/features/chart/lib/series-sources/analytics-book'
import {
  fetchAmberdataAnalyticRangeList,
  fetchAmberdataAnalyticSnapshotList,
  fetchSurfaceCurrencies,
} from '@/service/amberdata/analytics.api'
import {
  isAmberdataComparisonAnalyticsType,
  isAmberdataRangeAnalyticsType,
} from '@/service/amberdata/analytics.types'
import type { Exchange } from '@/types/generated/common/types'
import type {
  GetAnalyticRangeRequest,
  GetAnalyticSnapshotRequest,
} from '@/types/generated/public_api'

export const amberdataAnalyticsKeys = {
  all: ['amberdataAnalytics'] as const,
  snapshot: (params: GetAnalyticSnapshotRequest) =>
    [
      ...amberdataAnalyticsKeys.all,
      'snapshot',
      params.exchange,
      params.symbol,
      params.analyticsType,
    ] as const,
  range: (params: GetAnalyticRangeRequest) =>
    [
      ...amberdataAnalyticsKeys.all,
      'range',
      params.exchange,
      params.symbol,
      params.analyticsType,
      params.from,
      params.to,
      params.limit,
    ] as const,
}

export function useAmberdataAnalyticSnapshot(
  params: GetAnalyticSnapshotRequest,
  enabled = true,
  options: {
    refetchInterval?: number
    refetchOnMount?: boolean | 'always'
  } = {}
) {
  const book = useAnalyticsBook(params.exchange, params.symbol)
  const resolved = { ...params, symbol: book ?? '' }
  return useQuery({
    queryKey: amberdataAnalyticsKeys.snapshot(resolved),
    queryFn: ({ signal }) => fetchAmberdataAnalyticSnapshotList(resolved, signal),
    enabled:
      enabled &&
      Boolean(resolved.symbol) &&
      Boolean(params.exchange) &&
      Boolean(params.analyticsType),
    staleTime: 10 * 1000,
    refetchInterval: options.refetchInterval,
    refetchOnMount: options.refetchOnMount,
  })
}

export function useAmberdataAnalyticRange(
  params: GetAnalyticRangeRequest,
  enabled = true,
  options: { refetchInterval?: number } = {}
) {
  const book = useAnalyticsBook(params.exchange, params.symbol)
  const resolved = { ...params, symbol: book ?? '' }
  return useQuery({
    queryKey: amberdataAnalyticsKeys.range(resolved),
    queryFn: ({ signal }) => fetchAmberdataAnalyticRangeList(resolved, signal),
    enabled:
      enabled &&
      Boolean(resolved.symbol) &&
      Boolean(params.exchange) &&
      Boolean(params.analyticsType) &&
      (isAmberdataRangeAnalyticsType(params.analyticsType) ||
        isAmberdataComparisonAnalyticsType(params.analyticsType)),
    staleTime: 10 * 1000,
    refetchInterval: options.refetchInterval,
  })
}

export const surfaceCurrenciesQueryOptions = {
  queryKey: [...amberdataAnalyticsKeys.all, 'surfaceCurrencies'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => fetchSurfaceCurrencies(signal),
  // The server caches the list too; the book set changes on the order of days.
  staleTime: 10 * 60 * 1000,
  // No component observes this key, so without this it is collected after the 5 min default.
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
}

/**
 * The server's book name for a composed key on `exchange`. Undefined while the book list
 * loads (queries stay disabled); once loaded, a key with no book is sent as-is so the
 * request answers "no data" instead of hanging.
 */
export function useAnalyticsBook(exchange: Exchange, name: string): string | undefined {
  const { data, isPending } = useQuery(surfaceCurrenciesQueryOptions)
  if (!name || isPending) return undefined
  return (data && resolveAnalyticsBookByName(data, exchange, name)) ?? name
}
